import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Card,
  Typography,
  Tag,
  Space,
  Flex,
  InputNumber,
  App,
  Drawer,
  Alert,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import RobinPage from "../components/core/RobinPage";
import {
  useGetAllMyStoresQuery,
  useLedgerTransactionsLazyQuery,
  useDownloadLedgerTransactionsLazyQuery,
} from "../graphql/generated/hooks";
import {
  TransactionType,
  LedgerTransactionsFilter,
} from "../graphql/generated/schemas";
import type { LedgerTransactionsQuery } from "../graphql/generated/operations";
import {
  AmountSign,
  SortBy,
  SortOrder,
  FilterState,
  SortState,
  PaginationState,
} from "../types/transactions";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const OrderTransactionsDetails = ({
  orderId,
  formatAmount,
  getTypeColor,
  t,
}: any) => {
  const [fetchDetails, { data, loading }] = useLedgerTransactionsLazyQuery();

  useEffect(() => {
    if (orderId) {
      fetchDetails({
        variables: {
          pagination: { limit: 100, offset: 0 },
          filter: { orderId },
        },
        fetchPolicy: "network-only",
      });
    }
  }, [orderId, fetchDetails]);

  const items = data?.ledgerTransactions?.items || [];
  const sortedItems = [...items].sort((a, b) => {
    if (Number(a.amountMinor) > 0 && Number(b.amountMinor) <= 0) return -1;
    if (Number(a.amountMinor) <= 0 && Number(b.amountMinor) > 0) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const columns: ColumnsType<any> = [
    { title: t("label.transaction.id"), dataIndex: "id", key: "id", width: 80 },
    {
      title: t("label.transaction.amount"),
      dataIndex: "amountMinor",
      key: "amountMinor",
      width: 120,
      render: formatAmount,
    },
    {
      title: t("label.transaction.type"),
      dataIndex: "type",
      key: "type",
      render: (type: TransactionType) => (
        <Tag color={getTypeColor(type)}>
          {t(`transaction.type.${String(type).toLowerCase()}`)}
        </Tag>
      ),
    },
  ];

  const totalAmount = items.reduce(
    (acc, item) => acc + Number(item.amountMinor),
    0,
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t("label.transaction_details") || "Transaction Details"}
      </Typography.Title>
      <Alert
        message={
          <Typography.Text strong>
            {t("label.total_amount") || "Total Amount"}:{" "}
            {formatAmount(String(totalAmount))}
          </Typography.Text>
        }
        type={totalAmount >= 0 ? "success" : "error"}
        showIcon
      />
      <Table
        size="small"
        columns={columns}
        dataSource={sortedItems}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
    </Space>
  );
};

function TransactionsList() {
  const { message } = App.useApp();
  const { t, i18n } = useTranslation();
  const { data: storesData, loading: storesLoading } = useGetAllMyStoresQuery();
  const [fetchLedgerTransactions, { loading: queryLoading }] =
    useLedgerTransactionsLazyQuery();
  const [downloadLedgerTransactions, { loading: downloadQueryLoading }] =
    useDownloadLedgerTransactionsLazyQuery();

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: null,
    amountSign: null,
    minAmount: null,
    maxAmount: null,
    createdFrom: null,
    createdTo: null,
    types: [],
    orderId: null,
    storeId: null,
  });

  const [sort, setSort] = useState<SortState>({
    sortBy: SortBy.CREATED_AT,
    sortOrder: SortOrder.DESC,
  });

  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);

  const handleRowClick = (record: any) => {
    if (record.orderId) {
      setSelectedTransaction(record);
      setDrawerVisible(true);
    }
  };

  // State for the component

  const [data, setData] = useState<LedgerTransactionsQuery | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Combine loading states
  const loading = queryLoading;
  const downloadLoading = downloadQueryLoading;

  // Helper function to build filter input that matches GraphQL schema
  const buildFilterInput = (filters: FilterState): LedgerTransactionsFilter => {
    return {
      search: filters.search ?? undefined,
      amountSign: filters.amountSign ?? undefined,
      minAmount: filters.minAmount ?? undefined,
      maxAmount: filters.maxAmount ?? undefined,
      createdFrom: filters.createdFrom ?? undefined,
      createdTo: filters.createdTo ?? undefined,
      types: filters.types.length > 0 ? filters.types : undefined,
      orderId: filters.orderId ?? undefined,
      storeId: filters.storeId ?? undefined,
    };
  };

  // Make real GraphQL call using lazy query hook
  const fetchTransactions = useCallback(
    async (
      isInitialLoad = false,
      overridePagination?: { current: number; pageSize: number },
      overrideSort?: SortState,
    ) => {
      // Use override config if provided, else use state
      const currentPagination = overridePagination || pagination;
      const currentSort = overrideSort || sort;

      try {
        const { data: gqlData, error } = await fetchLedgerTransactions({
          variables: {
            pagination: {
              limit: currentPagination.pageSize,
              offset:
                (currentPagination.current - 1) * currentPagination.pageSize,
            },
            filter: isInitialLoad
              ? { types: [TransactionType.OrderCapture] }
              : buildFilterInput(filters),
            sort: {
              sortBy: currentSort.sortBy as any,
              sortOrder: currentSort.sortOrder as any,
            },
          },
          fetchPolicy: "network-only",
        });

        if (error) {
          throw error;
        }

        // Set the data from GraphQL response
        if (gqlData) {
          setData(gqlData);
        }
        setHasSearched(true);
        if (isInitialLoad) {
          setInitialLoad(false);
        }
      } catch (error: any) {
        // Check if it's a GraphQL error or network error
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          // Handle GraphQL errors (like query not implemented yet)
          const gqlError = error.graphQLErrors[0];
          if (
            gqlError.message.includes("Cannot query field") ||
            gqlError.message.includes("Unknown")
          ) {
            message.warning(t("error.ledger_transactions_not_implemented"));
          } else {
            message.error(
              gqlError.message || t("error.failed_to_fetch_transactions"),
            );
          }
        } else if (error.networkError) {
          // Handle network errors
          message.error(t("error.network_error"));
        } else {
          // Handle other errors
          message.error(
            error.message || t("error.failed_to_fetch_transactions"),
          );
        }

        // Set empty data on error
        setData({
          __typename: "Query",
          ledgerTransactions: {
            __typename: "LedgerTransactionsPage",
            hasMore: false,
            total: 0,
            items: [],
          },
        });
        setHasSearched(true);
        if (isInitialLoad) {
          setInitialLoad(false);
        }
      }
    },
    [fetchLedgerTransactions, pagination, filters, sort, t, message],
  );

  // Download transactions as Excel file
  const handleDownload = useCallback(async () => {
    try {
      const { data: downloadData, error } = await downloadLedgerTransactions({
        variables: {
          filter: {
            search: filters.search,
            amountSign: filters.amountSign,
            minAmount: filters.minAmount,
            maxAmount: filters.maxAmount,
            createdFrom: filters.createdFrom,
            createdTo: filters.createdTo,
            types: filters.types.length > 0 ? filters.types : null,
            orderId: filters.orderId,
            storeId: filters.storeId,
            // Fields not in current filters - set to null
            availableFrom: null,
            availableTo: null,
            partnerId: null,
            postedFrom: null,
            postedTo: null,
            statuses: null,
          },
        },
        fetchPolicy: "network-only",
      });

      if (error) {
        throw error;
      }

      if (downloadData?.downloadLedgerTransactions) {
        const { fileContent, filename, mimeType } =
          downloadData.downloadLedgerTransactions;

        // Convert base64 to blob
        const byteCharacters = atob(fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success(t("success.file_downloaded"));
      }
    } catch (error: any) {
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const gqlError = error.graphQLErrors[0];
        if (
          gqlError.message.includes("Cannot query field") ||
          gqlError.message.includes("Unknown")
        ) {
          message.warning(t("error.download_not_implemented"));
        } else {
          message.error(gqlError.message || t("error.failed_to_download"));
        }
      } else if (error.networkError) {
        message.error(t("error.network_error"));
      } else {
        message.error(error.message || t("error.failed_to_download"));
      }
    }
  }, [downloadLedgerTransactions, filters, t, message]);

  // Load data on component mount
  useEffect(() => {
    if (initialLoad) {
      fetchTransactions(true);
    }
  }, [initialLoad, fetchTransactions]);

  // Update pagination total when data changes
  useEffect(() => {
    if (data?.ledgerTransactions) {
      setPagination((prev) => ({
        ...prev,
        total: data.ledgerTransactions.total,
      }));
    }
  }, [data]);

  // Filter handlers
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handleSortChange = (newSort: Partial<SortState>) => {
    setSort((prev) => ({ ...prev, ...newSort }));
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page
  };

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: any,
    sorter: any,
  ) => {
    let nextPagination = {
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    // Handle pagination
    if (paginationConfig.current && paginationConfig.pageSize) {
      nextPagination = {
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination((prev) => ({
        ...prev,
        ...nextPagination,
      }));
    }

    let nextSort = { ...sort };
    // Handle sorting from table headers
    if (sorter && sorter.field) {
      const sortByMap: Record<string, SortBy> = {
        amountMinor: SortBy.AMOUNT,
        createdAt: SortBy.CREATED_AT,
      };

      const sortBy = sortByMap[sorter.field] || SortBy.CREATED_AT;
      const sortOrder =
        sorter.order === "ascend" ? SortOrder.ASC : SortOrder.DESC;

      nextSort = { sortBy, sortOrder };
      setSort(nextSort);
    }

    // Only refetch if we have already searched before
    if (hasSearched) {
      fetchTransactions(false, nextPagination, nextSort); // Delay to ensure state updates are applied
    }
  };

  const resetFilters = () => {
    setFilters({
      search: null,
      amountSign: null,
      minAmount: null,
      maxAmount: null,
      createdFrom: null,
      createdTo: null,
      types: [TransactionType.OrderCapture],
      orderId: null,
      storeId: null,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    // Load all data without filters (like initial load)
    fetchTransactions(true);
  };

  // Format currency amount
  const formatAmount = (amountMinor: string) => {
    const amount = parseFloat(amountMinor);
    const isNegative = amount < 0;
    const absoluteAmount = Math.abs(amount);

    return (
      <Text type={isNegative ? "danger" : "success"}>
        {isNegative ? "-" : "+"} {absoluteAmount.toFixed(2)} {t("label.sar")}
      </Text>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("YYYY-MM-DD HH:mm:ss");
  };

  // Get transaction type color
  const getTypeColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.OrderCapture:
        return "blue";
      case TransactionType.PlatformCommission:
        return "red";
      case TransactionType.DeliveryFee:
        return "purple";
      case TransactionType.GatewayFee:
        return "orange";
      case TransactionType.Payout:
        return "volcano";
      case TransactionType.Refund:
        return "magenta";
      case TransactionType.Adjustment:
        return "cyan";
      case TransactionType.Tip:
        return "green";
      case TransactionType.PartnerDeliveryCoverage:
        return "geekblue";
      case TransactionType.DiscountPartnerCoverage:
        return "gold";
      case TransactionType.AdditionalCustomerFee:
        return "lime";
      case TransactionType.FastDeliveryFee:
        return "purple";
      case TransactionType.DistanceFee:
        return "blue";
      default:
        return "default";
    }
  };

  // Table columns
  const columns: ColumnsType<
    LedgerTransactionsQuery["ledgerTransactions"]["items"][0]
  > = [
    {
      title: t("label.transaction.order_id"),
      dataIndex: "orderId",
      key: "orderId",
      width: 120,
      render: (orderId: number) =>
        orderId ? (
          <Space>
            <Typography.Text strong style={{ color: "#1677ff" }}>
              #{orderId}
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: t("label.transaction.amount"),
      dataIndex: "amountMinor",
      key: "amountMinor",
      width: 120,
      render: formatAmount,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: t("label.transaction.type"),
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type: TransactionType) => (
        <Tag color={getTypeColor(type)}>
          {t(`transaction.type.${String(type).toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t("label.transaction.description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: t("label.transaction.store_name"),
      dataIndex: "store",
      key: "store",
      width: 150,
      render: (store: { ar: string; en: string }) => {
        const currentLang = i18n.language;
        const storeName = currentLang === "ar" ? store.ar : store.en;
        return storeName || store.en || store.ar; // Fallback to available language
      },
    },
    {
      title: t("label.transaction.created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: formatDate,
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
  ];

  return (
    <RobinPage title={t("label.menu.transactions")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Filters */}
        <Card title={t("label.filters")} size="small">
          <Row gutter={[16, 16]}>
            {/* Search */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input.Search
                placeholder={t("placeholder.search_transactions")}
                allowClear
                value={filters.search || ""}
                onChange={(e) =>
                  handleFilterChange("search", e.target.value || null)
                }
                onSearch={() => fetchTransactions()}
              />
            </Col>

            {/* Transaction Types */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                mode="multiple"
                placeholder={t("label.transaction.type")}
                allowClear
                style={{ width: "100%" }}
                value={filters.types}
                onChange={(value) => handleFilterChange("types", value)}
              >
                {Object.values(TransactionType).map((type) => (
                  <Option key={type} value={type}>
                    {t(`transaction.type.${type.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Amount Sign */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.transaction.amount_sign")}
                allowClear
                style={{ width: "100%" }}
                value={filters.amountSign}
                onChange={(value) => handleFilterChange("amountSign", value)}
              >
                <Option value={AmountSign.POSITIVE}>
                  {t("label.positive")}
                </Option>
                <Option value={AmountSign.NEGATIVE}>
                  {t("label.negative")}
                </Option>
              </Select>
            </Col>

            {/* Min Amount */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.min_amount")}
                style={{ width: "100%" }}
                value={filters.minAmount}
                onChange={(value) => handleFilterChange("minAmount", value)}
                prefix={t("label.sar")}
                step={0.01}
                precision={2}
              />
            </Col>

            {/* Max Amount */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.max_amount")}
                style={{ width: "100%" }}
                value={filters.maxAmount}
                onChange={(value) => handleFilterChange("maxAmount", value)}
                prefix={t("label.sar")}
                step={0.01}
                precision={2}
              />
            </Col>

            {/* Order ID */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <InputNumber
                placeholder={t("label.transaction.order_id")}
                style={{ width: "100%" }}
                value={filters.orderId}
                onChange={(value) => handleFilterChange("orderId", value)}
              />
            </Col>

            {/* Store ID */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.transaction.store_id")}
                allowClear
                style={{ width: "100%" }}
                value={filters.storeId}
                onChange={(value) => handleFilterChange("storeId", value)}
                loading={storesLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={storesData?.getAllStoresForPartner?.map((store) => ({
                  label: `${store.name} (ID: ${store.id})`,
                  value: store.id,
                }))}
              />
            </Col>

            {/* Created Date Range */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                placeholder={[
                  t("label.transaction.created_from"),
                  t("label.transaction.created_to"),
                ]}
                style={{ width: "100%" }}
                value={
                  filters.createdFrom && filters.createdTo
                    ? [dayjs(filters.createdFrom), dayjs(filters.createdTo)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    handleFilterChange("createdFrom", dates[0].toISOString());
                    handleFilterChange("createdTo", dates[1].toISOString());
                  } else {
                    handleFilterChange("createdFrom", null);
                    handleFilterChange("createdTo", null);
                  }
                }}
                showTime
              />
            </Col>

            {/* Sort Controls */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.sort_by")}
                style={{ width: "100%" }}
                value={sort.sortBy}
                onChange={(value) => handleSortChange({ sortBy: value })}
              >
                <Option value={SortBy.AMOUNT}>
                  {t("label.transaction.amount")}
                </Option>
                <Option value={SortBy.CREATED_AT}>
                  {t("label.transaction.created_at")}
                </Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder={t("label.sort_order")}
                style={{ width: "100%" }}
                value={sort.sortOrder}
                onChange={(value) => handleSortChange({ sortOrder: value })}
              >
                <Option value={SortOrder.ASC}>{t("label.ascending")}</Option>
                <Option value={SortOrder.DESC}>{t("label.descending")}</Option>
              </Select>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={24} md={24} lg={24}>
              <Flex gap="small" wrap="wrap">
                <Button
                  type="primary"
                  onClick={() => fetchTransactions()}
                  loading={loading}
                >
                  {t("button.search")}
                </Button>
                <Button onClick={resetFilters}>
                  {t("button.reset_filters")}
                </Button>
                <Button
                  type="default"
                  onClick={handleDownload}
                  loading={downloadLoading}
                  disabled={loading}
                >
                  {t("button.download")}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Card>

        {/* Transactions Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={data?.ledgerTransactions?.items || []}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1400 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t("label.transactions")}`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: record.orderId ? "pointer" : "default" },
            })}
          />
        </Card>
      </Space>

      <Drawer
        title={t("label.transaction_details") || "Transaction Details"}
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
      >
        {selectedTransaction?.orderId && (
          <OrderTransactionsDetails
            orderId={selectedTransaction.orderId}
            formatAmount={formatAmount}
            getTypeColor={getTypeColor}
            t={t}
          />
        )}
      </Drawer>
    </RobinPage>
  );
}

export default TransactionsList;
